import DataGridTable from '@/components/datagridTable';
import DrawerPanel from '@/components/drawerPanel';
import ErrorMessage from '@/components/errorMessage';
import ProgressIndicator from '@/components/progressIndicator';
import { useApiContext } from '@/contexts/apiContext';
import { useUserContext } from '@/contexts/userContext';
import LaunchIcon from '@mui/icons-material/Launch';
import { Box, Button, Stack, Typography } from '@mui/material';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ContactUsForm from '@/components/contactUs/contactUsForm';
import { formatRelative } from 'date-fns';

const AgentDefs = () => {
  const apiContext = useApiContext();
  const { hasRole } = useUserContext();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isAdmin = hasRole('administrator');
  const [agents, setAgents] = useState<any>([]);
  const [apiError, setApiError] = useState<any>();
  const [loading, setLoading] = useState(false);
  const [showContactUs, setShowContactUs] = useState(false);

  const hideContactUs = () => {
    setShowContactUs(false);
  };

  useEffect(() => {
    const getAgents = async () => {
      try {
        setLoading(true);
        const agents = await apiContext.getAgentDefs();
        setAgents(agents || []);
      } catch (error: any) {
        setApiError(error);
      } finally {
        setLoading(false);
      }
    };
    getAgents();
  }, []);

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: t('agent.agentDefs.columns.id'),
      width: 200,
    },
    {
      field: 'name',
      headerName: t('agent.agentDefs.columns.name'),
      flex: 1,
    },
    {
      field: 'domainId',
      headerName: 'Domain',
      width: 140,
      valueFormatter: (value: string) => value || 'general',
    },
    {
      field: 'implementationStack',
      headerName: t('agent.agentDefs.columns.implementationStack'),
      width: 200,
    },
    {
      field: 'createdBy',
      headerName: t('agent.agentDefs.columns.createdBy'),
      width: 200,
      valueFormatter: (value: string) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : ''),
    },
    {
      field: 'createdDate',
      headerName: t('agent.agentDefs.columns.createdDate'),
      width: 200,
      valueFormatter: (value: string) => (value ? formatRelative(new Date(value), new Date()) : ''),
    },
    {
      field: 'actions',
      type: 'actions',
      width: 50,
      getActions: (params: any) => [
        <GridActionsCellItem icon={<LaunchIcon />} label="Open" onClick={() => openAgentDef(params.row)} />,
      ],
    },
  ];

  const openAgentDef = (agentDef: any) => {
    navigate(isAdmin ? `/agentDef/${agentDef.id}` : `/agentDef/${agentDef.id}/view`);
  };

  const createAgent = () => {
    navigate(`/newAgentDef`);
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {/* Flat Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ px: 2.5, py: 1.25, borderBottom: 1, borderColor: 'divider', flexShrink: 0, bgcolor: 'background.paper' }}
      >
        <Stack spacing={0.25}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {t('agent.agentDefs.header')}
          </Typography>
          {apiError && <ErrorMessage error={apiError} />}
        </Stack>
        {isAdmin && (
          <Button
            size="small"
            color="secondary"
            variant="contained"
            onClick={createAgent}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '8px',
              boxShadow: 'none',
              color: 'text.primary',
            }}
          >
            {t('agent.agentDefs.actions.create')}
          </Button>
        )}
      </Stack>

      {/* Content Area */}
      <Box sx={{ flex: 1, overflow: 'hidden', p: 1, display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <ProgressIndicator />
        ) : (
          <>
            {agents.length === 0 && <Typography sx={{ p: 1 }}>{t('agent.agentDefs.noRecord')}</Typography>}
            {agents.length > 0 && (
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <DataGridTable rows={agents} columns={columns} loading={loading} pageSize={10} />
              </Box>
            )}
          </>
        )}
      </Box>

      <DrawerPanel open={showContactUs} onClose={hideContactUs} minWidth={'40vw'}>
        <ContactUsForm onClose={() => setShowContactUs(false)} />
      </DrawerPanel>
    </Box>
  );
};

export default AgentDefs;
